import React, { useState, useEffect } from 'react';
import { Modal, Card, Row, Col, Table, Button, Badge, Form, ButtonGroup } from 'react-bootstrap';
import { quoteService } from '../../services/api';
import { Quote } from '../../types';
import { toast } from 'react-toastify';
import StatusBadge from '../common/StatusBadge';

interface QuoteDetailsProps {
  show: boolean;
  onHide: () => void;
  quoteId: number | null;
  onUpdate: () => void;
}

const QuoteDetails: React.FC<QuoteDetailsProps> = ({ show, onHide, quoteId, onUpdate }) => {
  const [quote, setQuote] = useState<Quote | null>(null);
  const [loading, setLoading] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    if (show && quoteId) {
      fetchQuote();
    }
  }, [show, quoteId]);

  const fetchQuote = async () => {
    if (!quoteId) return;
    
    try {
      setLoading(true);
      const response = await quoteService.getById(quoteId);
      setQuote(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement du devis');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!quote) return;

    try {
      setUpdatingStatus(true);
      const response = await quoteService.updateStatus(quote.id, newStatus);
      toast.success(response.data.message);
      setQuote(response.data.quote);
      onUpdate();
    } catch (error: any) {
      const message = error.response?.data?.error || 'Erreur lors de la mise à jour du statut';
      toast.error(message);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleConvertToInvoice = async () => {
    if (!quote) return;

    try {
      setConverting(true);
      const response = await quoteService.convertToInvoice(quote.id);
      toast.success(response.data.message);
      setQuote(response.data.quote);
      onUpdate();
    } catch (error: any) {
      console.error('Erreur conversion:', error);
      const message = error.response?.data?.error || error.message || 'Erreur lors de la conversion';
      toast.error(message);
    } finally {
      setConverting(false);
    }
  };

  const handleExportPdf = async () => {
    if (!quote) return;

    try {
      const response = await quoteService.generatePdf(quote.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `devis-${quote.quote_number}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erreur lors de l\'export PDF');
    }
  };



  const getStatusActions = () => {
    if (!quote) return [];

    const actions = [];
    
    switch (quote.status) {
      case 'draft':
        actions.push(
          <Button 
            key="send" 
            variant="primary" 
            size="sm" 
            onClick={() => handleStatusChange('sent')}
            disabled={updatingStatus}
          >
            Marquer comme envoyé
          </Button>
        );
        break;
      
      case 'sent':
        actions.push(
          <Button 
            key="accept" 
            variant="success" 
            size="sm" 
            onClick={() => handleStatusChange('accepted')}
            disabled={updatingStatus}
            className="me-2"
          >
            Accepter
          </Button>,
          <Button 
            key="reject" 
            variant="danger" 
            size="sm" 
            onClick={() => handleStatusChange('rejected')}
            disabled={updatingStatus}
          >
            Refuser
          </Button>
        );
        break;
      
      case 'accepted':
        actions.push(
          <Button 
            key="convert" 
            variant="warning" 
            size="sm" 
            onClick={handleConvertToInvoice}
            disabled={converting}
          >
            {converting ? 'Conversion...' : 'Convertir en facture'}
          </Button>
        );
        break;
      
      case 'rejected':
        actions.push(
          <Button 
            key="reopen" 
            variant="outline-primary" 
            size="sm" 
            onClick={() => handleStatusChange('draft')}
            disabled={updatingStatus}
          >
            Remettre en brouillon
          </Button>
        );
        break;
    }

    return actions;
  };

  if (loading) {
    return (
      <Modal show={show} onHide={onHide} size="xl">
        <Modal.Body className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  if (!quote) return null;

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Détails du devis {quote.quote_number}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-4">
          <Col md={6}>
            <Card>
              <Card.Header>
                <h6 className="mb-0">Informations générales</h6>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col sm={4}><strong>Numéro:</strong></Col>
                  <Col sm={8}>{quote.quote_number}</Col>
                </Row>
                <Row>
                  <Col sm={4}><strong>Client:</strong></Col>
                  <Col sm={8}>{quote.client?.name}</Col>
                </Row>
                <Row>
                  <Col sm={4}><strong>Date:</strong></Col>
                  <Col sm={8}>{new Date(quote.date).toLocaleDateString('fr-FR')}</Col>
                </Row>
                <Row>
                  <Col sm={4}><strong>Valide jusqu'au:</strong></Col>
                  <Col sm={8}>{new Date(quote.valid_until).toLocaleDateString('fr-FR')}</Col>
                </Row>
                <Row>
                  <Col sm={4}><strong>Statut:</strong></Col>
                  <Col sm={8}><StatusBadge status={quote.status} type="quote" /></Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card>
              <Card.Header>
                <h6 className="mb-0">Montants</h6>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col sm={6}><strong>Sous-total HT:</strong></Col>
                  <Col sm={6} className="text-end">
                    {new Intl.NumberFormat('fr-FR', {
                      style: 'currency',
                      currency: 'MAD'
                    }).format(quote.subtotal_ht)}
                  </Col>
                </Row>
                <Row>
                  <Col sm={6}><strong>TVA:</strong></Col>
                  <Col sm={6} className="text-end">
                    {new Intl.NumberFormat('fr-FR', {
                      style: 'currency',
                      currency: 'MAD'
                    }).format(quote.total_vat)}
                  </Col>
                </Row>
                <Row className="border-top pt-2">
                  <Col sm={6}><strong>Total TTC:</strong></Col>
                  <Col sm={6} className="text-end">
                    <strong>
                      {new Intl.NumberFormat('fr-FR', {
                        style: 'currency',
                        currency: 'MAD'
                      }).format(quote.total_ttc)}
                    </strong>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="mb-4">
          <Card.Header>
            <h6 className="mb-0">Articles</h6>
          </Card.Header>
          <Card.Body>
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Produit/Service</th>
                  <th>Description</th>
                  <th>Quantité</th>
                  <th>Prix unitaire</th>
                  <th>TVA</th>
                  <th>Total HT</th>
                  <th>Total TTC</th>
                </tr>
              </thead>
              <tbody>
                {quote.items?.map((item, index) => (
                  <tr key={index}>
                    <td>{item.product_name}</td>
                <td
                dangerouslySetInnerHTML={{
                  __html: item.description ? item.description : ''
                }}
              />
                    <td>{item.quantity} {item.unit}</td>
                    <td>
                      {new Intl.NumberFormat('fr-FR', {
                        style: 'currency',
                        currency: 'MAD'
                      }).format(item.unit_price)}
                    </td>
                    <td>{item.vat_rate}%</td>
                    <td>
                      {new Intl.NumberFormat('fr-FR', {
                        style: 'currency',
                        currency: 'MAD'
                      }).format(item.total_ht)}
                    </td>
                    <td>
                      {new Intl.NumberFormat('fr-FR', {
                        style: 'currency',
                        currency: 'MAD'
                      }).format(item.total_ttc)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        {(quote.notes || quote.terms) && (
          <Row>
            {quote.notes && (
              <Col md={6}>
                <Card>
                  <Card.Header>
                    <h6 className="mb-0">Notes</h6>
                  </Card.Header>
                  <Card.Body>
                    <p className="mb-0">{quote.notes}</p>
                  </Card.Body>
                </Card>
              </Col>
            )}
            {quote.terms && (
              <Col md={6}>
                <Card>
                  <Card.Header>
                    <h6 className="mb-0">Conditions</h6>
                  </Card.Header>
                  <Card.Body>
                    <p className="mb-0">{quote.terms}</p>
                  </Card.Body>
                </Card>
              </Col>
            )}
          </Row>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <div>
            <ButtonGroup>
              {getStatusActions()}
            </ButtonGroup>
          </div>
          <div>
            <Button variant="outline-primary" onClick={handleExportPdf} className="me-2">
              Télécharger PDF
            </Button>
            <Button variant="secondary" onClick={onHide}>
              Fermer
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default QuoteDetails;